import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { useTranslation } from 'react-i18next';
import salesService from '../../../../services/salesService';
import { restaurantScreenApi } from '../../../../services/restaurantApiService';

interface ScreenOption {
  id: number;
  name: string;
  branchId: string | null;
}

interface Branch {
  id?: string;
  branchId?: string;
  name: string;
}

export interface SelectedScreen {
  id: number;
  name: string;
}

export type ScreenSelectMode = 'quickSale' | 'restaurant' | 'package';

interface ScreenSelectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Hangi ekran türü listelenecek: hızlı satış (Standalone), restoran veya paket sipariş */
  mode: ScreenSelectMode;
  /** Kayıtlı seçim (ekran Id listesi) */
  selectedIds: number[];
  /** Kaydet'e basılınca yeni seçim (id + ad; kart başlıklarında ad gösterilir) */
  onSave: (screens: SelectedScreen[]) => void;
  branches: Branch[];
}

/**
 * Dashboard "Ekran Bazlı Satışlar" kart takımları için ekran seçimi.
 * mode='quickSale': aktif Standalone QuickSaleScreen'ler,
 * mode='restaurant': aktif RestaurantScreen'ler,
 * mode='package': aktif PackageScreen'ler listelenir; çoklu seçim yapılabilir.
 * Seçim ilgili 3 kartı (istatistik + widget + grafik) birden etkiler.
 */
const ScreenSelectDialog: React.FC<ScreenSelectDialogProps> = ({
  open,
  onClose,
  mode,
  selectedIds,
  onSave,
  branches,
}) => {
  const { t } = useTranslation('accounting');
  const [screens, setScreens] = useState<ScreenOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState<number[]>(selectedIds);

  // Dialog her açıldığında kayıtlı seçimle senkronize başla
  useEffect(() => {
    if (open) setChecked(selectedIds);
  }, [open, selectedIds]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        let options: ScreenOption[];
        if (mode === 'restaurant') {
          // Aktif restoran ekranları
          const all = await restaurantScreenApi.getByCompanyId();
          options = all
            .filter((s) => s.IsActive !== false)
            .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId || null }));
        } else if (mode === 'package') {
          // Aktif paket sipariş ekranları
          const all = await salesService.getPackageScreens();
          options = all
            .filter((s) => s.IsActive !== false)
            .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId || null }));
        } else {
          // QuickSalePage ile aynı filtre: aktif + Standalone ekranlar
          const all = await salesService.getQuickSaleScreens();
          options = all
            .filter((s) => s.IsActive !== false && (!s.ScreenType || s.ScreenType === 'Standalone'))
            .map((s) => ({ id: s.Id, name: s.Name, branchId: s.BranchId || null }));
        }
        options.sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setScreens(options);
      } catch (error) {
        console.error('❌ [DASHBOARD] Ekran listesi yüklenemedi:', error);
        if (!cancelled) setScreens([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode]);

  const getBranchName = (branchId: string | null): string | null => {
    if (!branchId) return null;
    return branches.find((b) => (b.id || b.branchId) === branchId)?.name || null;
  };

  const toggle = (id: number) => {
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'restaurant'
            ? <TableRestaurantIcon color="primary" />
            : mode === 'package'
              ? <DeliveryDiningIcon color="primary" />
              : <PointOfSaleIcon color="primary" />}
          <Typography variant="h6" component="span" fontWeight="bold">
            {mode === 'restaurant'
              ? t('dashboard.screenSelect.titleRestaurant')
              : mode === 'package'
                ? t('dashboard.screenSelect.titlePackage')
                : t('dashboard.screenSelect.title')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography variant="body2" color="textSecondary" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          {mode === 'restaurant'
            ? t('dashboard.screenSelect.descriptionRestaurant')
            : mode === 'package'
              ? t('dashboard.screenSelect.descriptionPackage')
              : t('dashboard.screenSelect.description')}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : screens.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard.screenSelect.noScreens')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ pb: 1 }}>
            {screens.map((screen) => {
              const branchName = getBranchName(screen.branchId);
              return (
                <ListItemButton key={screen.id} onClick={() => toggle(screen.id)} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={checked.includes(screen.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={screen.name}
                    secondary={branchName || undefined}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('dashboard.screenSelect.cancel')}</Button>
        <Button
          variant="contained"
          disabled={checked.length === 0}
          onClick={() => {
            const selected = screens
              .filter((s) => checked.includes(s.id))
              .map((s) => ({ id: s.id, name: s.name }));
            onSave(selected);
            onClose();
          }}
        >
          {t('dashboard.screenSelect.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScreenSelectDialog;
